// Ring — a calm circular progress dial (e.g. attention count, health score).
// Inline SVG, no chart libraries. The arc uses the petrol brand stroke; the
// center holds a count/label. Colorblind-safe (single hue + numeric center).

export type RingProps = {
  /** Fraction filled, 0..1 (values outside are clamped). */
  value: number
  /** Center content (number or short label). */
  label?: string | number
  size?: number
  stroke?: number
  ariaLabel?: string
  /** Override arc color (defaults to brand petrol). */
  color?: string
  className?: string
}

export function Ring({ value, label, size = 88, stroke = 8, ariaLabel, color = 'var(--brand, #1b4965)', className }: RingProps) {
  const clamped = Math.max(0, Math.min(1, Number.isFinite(value) ? value : 0))
  const r = (size - stroke) / 2
  const cx = size / 2
  const cy = size / 2
  const circ = 2 * Math.PI * r
  const dash = circ * clamped
  return (
    <div className={className} style={{ position: 'relative', width: size, height: size, flex: 'none' }}>
      <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size} role="img" aria-label={ariaLabel || `Ring ${(clamped * 100).toFixed(0)}%`}>
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="var(--anchor-soft, #ece9e1)" strokeWidth={stroke} />
        <circle
          cx={cx}
          cy={cy}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={`${dash.toFixed(2)} ${(circ - dash).toFixed(2)}`}
          transform={`rotate(-90 ${cx} ${cy})`}
        />
      </svg>
      {label != null ? (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'grid',
            placeItems: 'center',
            fontFamily: 'var(--font-guide)',
            fontSize: size > 70 ? 32 : 20,
            fontWeight: 600,
            color: 'var(--ink, #1c1f26)',
          }}
        >
          {label}
        </div>
      ) : null}
    </div>
  )
}

export default Ring
